/**
 * نظام الألوان المركزي للموقع
 * Centralized Color System
 * 
 * جميع الألوان المستخدمة في الموقع موجودة هنا
 * All website colors are centralized here
 */

// Theme definitions - تعريفات الثيمات
const themeDefinitions = {
  green: {
    name: 'green',
    displayName: 'أخضر',
    primary: {
      light: '#4db693',      // green-300 from TeaminLandingPage
      main: '#0C8862',        // green-500 - Main primary color from TeaminLandingPage
      dark: '#0B815C',        // green-600 from TeaminLandingPage
      bright: '#0C8E67',      // green-400 from TeaminLandingPage
      lighter: '#80cbb2',     // green-200 from TeaminLandingPage
      darkest: '#0B7654',    // green-700 from TeaminLandingPage
    },
    gradients: {
      primary: 'linear-gradient(135deg, #4db693 0%, #0C8862 100%)',
      primaryEmerald: 'linear-gradient(135deg, #4db693 0%, #0C8862 50%, #0C8E67 100%)',
      light: 'linear-gradient(135deg, #0C8E67 0%, #0B815C 100%)',
      button: 'linear-gradient(to right, #4db693, #0C8862)',
      sidebarActive: 'linear-gradient(135deg, rgba(77, 182, 147, 1) 0%, rgba(12, 136, 98, 1) 50%, rgba(12, 142, 103, 1) 100%)',
      text: 'linear-gradient(135deg, #4db693 0%, #0C8862 100%)',
      background: 'linear-gradient(135deg, rgba(230, 245, 240, 0.8) 0%, rgba(179, 224, 209, 0.8) 100%)',
    },
    rgb: {
      primary: {
        light: 'rgba(77, 182, 147, 1)',
        main: 'rgba(12, 136, 98, 1)',
        bright: 'rgba(12, 142, 103, 1)',
        lightAlpha: (opacity) => `rgba(77, 182, 147, ${opacity})`,
        mainAlpha: (opacity) => `rgba(12, 136, 98, ${opacity})`,
        brightAlpha: (opacity) => `rgba(12, 142, 103, ${opacity})`,
      },
    },
    tailwind: {
      from: 'from-green-400',
      to: 'to-emerald-500',
      via: 'via-emerald-50',
      bgFrom: 'from-green-50',
      bgTo: 'to-emerald-50',
      buttonFrom: 'from-green-500',
      buttonTo: 'to-emerald-600',
      buttonHoverFrom: 'hover:from-green-600',
      buttonHoverTo: 'hover:to-emerald-700',
    },
    shadows: {
      glow: '0 0 20px rgba(77, 182, 147, 0.3)',
      glowStrong: '0 0 30px rgba(77, 182, 147, 0.6)',
      card: '0 4px 15px rgba(77, 182, 147, 0.4)',
      cardStrong: '0 6px 25px rgba(77, 182, 147, 0.5)',
      button: '0 0 20px rgba(12, 136, 98, 0.3)',
      logo: '0 4px 14px 0 rgba(77, 182, 147, 0.15)',
      logoHover: '0 8px 25px 0 rgba(77, 182, 147, 0.25)',
    },
    border: {
      green: '#80cbb2',
      greenDark: '#0B815C',
    },
    background: {
      green50: '#e6f5f0',
      green100: '#b3e0d1',
      emerald50: '#e6f5f0',
    },
  },
};

// Common colors shared across all themes - الألوان المشتركة بين جميع الثيمات
const commonColors = {
  secondary: {
    success: '#0C8862',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    muted: '#9ca3af',
    light: '#ffffff',
    dark: '#111827',
  },
  background: {
    light: '#ffffff',
    dark: '#111827',
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
  },
  border: {
    light: '#e5e7eb',
    default: '#d1d5db',
    dark: '#374151',
  },
  dark: {
    primary: {
      light: '#4db693',
      main: '#0C8862',
      dark: '#0B815C',
    },
    background: {
      main: '#111827',
      secondary: '#1f2937',
      tertiary: '#374151',
    },
    text: {
      primary: '#f9fafb',
      secondary: '#d1d5db',
      muted: '#9ca3af',
    },
  },
};

// Get current theme colors - الحصول على ألوان الثيم الحالي
export const getThemeColors = (themeName = 'green') => {
  const theme = themeDefinitions[themeName] || themeDefinitions.green;
  
  return {
    ...theme,
    ...commonColors,
    // Merge theme-specific background and border with common ones
    background: {
      ...commonColors.background,
      ...theme.background,
    },
    border: {
      ...commonColors.border,
      ...theme.border,
    },
  };
};

// Export all available themes - تصدير جميع الثيمات المتاحة
export const themes = themeDefinitions;

// Export theme list for selector - تصدير قائمة الثيمات للاختيار
export const themeList = Object.values(themeDefinitions).map(theme => ({
  name: theme.name,
  displayName: theme.displayName,
}));

// Get current theme from localStorage (for static imports)
// Always return 'green' as it's the only available theme
const getCurrentThemeName = () => {
  // Always use green theme - no other themes available
  return 'green';
};

// Default export for backward compatibility - التصدير الافتراضي للتوافق مع الكود القديم
// This uses the current theme from localStorage, or green as default
// NOTE: For dynamic theme updates, use useTheme().colors from ThemeContext instead
export const colors = (() => {
  // Return a getter that reads from localStorage on each access
  // But for performance, we'll use a proxy or just return the current theme
  const currentTheme = getCurrentThemeName();
  return getThemeColors(currentTheme);
})();

// دوال مساعدة - Helper Functions
export const getGradient = (type = 'primary') => {
  return colors.gradients[type] || 
  colors.gradients.primary;
};

export const getRgba = (colorType, opacity = 1) => {
  if (typeof colors.rgb.primary[colorType] === 'function') {
    return colors.rgb.primary[colorType](opacity);
  }
  return colors.rgb.primary[colorType] || colors.rgb.primary.light;
};

export const getShadow = (type = 'glow') => {
  return colors.shadows[type] || colors.shadows.glow;
};

// للاستخدام مع styled-components أو CSS-in-JS
export const theme = {
  colors,
  getGradient,
  getRgba,
  getShadow,
};

export default colors;

